export const LIVE_OPERATIONAL_EVENTS=Object.freeze([
  'live_action_result',
  'live_sync_result',
  'live_sync_recovery',
  'live_notification_interaction'
]);

const ALLOWED=new Set(LIVE_OPERATIONAL_EVENTS);
const ACTIONS=new Set([
  'session_start',
  'session_complete',
  'stage_transition',
  'custom_stage_insert',
  'temperature_log',
  'event_log',
  'note_capture',
  'photo_capture',
  'notification_configure'
]);
const NOTIFICATION_KINDS=new Set(['stage_end','temperature_check']);

function hash32(value){
  let hash=0x811c9dc5;
  for(const char of String(value||'')){
    hash^=char.codePointAt(0);
    hash=Math.imul(hash,0x01000193);
  }
  return hash>>>0;
}

export function liveSessionRef(sessionId){
  const value=String(sessionId||'').trim();
  if(!value)return null;
  const left=hash32('dymistrz:a:'+value).toString(36).padStart(7,'0');
  const right=hash32('dymistrz:b:'+value).toString(36).padStart(7,'0');
  return 'live_'+left+right;
}

function bounded(value,max=100000){
  const number=Number(value);
  if(!Number.isFinite(number))return 0;
  return Math.max(0,Math.min(max,Math.floor(number)));
}

function errorCode(value){
  const text=String(value||'').trim();
  if(!text)return null;
  if(!/^[A-Z][A-Z0-9_.:-]{0,79}$/.test(text))return 'UNCLASSIFIED_ERROR';
  return text;
}

function contextValue(value,fallback='unknown'){
  const text=String(value||fallback).trim();
  return (text||fallback).slice(0,80);
}

function diagnosticsOutcome(name,properties){
  if(name==='live_action_result')return properties.outcome;
  if(name==='live_sync_result'){
    if(properties.status==='success')return 'success';
    if(properties.status==='partial')return 'partial';
    if(properties.status==='offline')return 'offline';
    return 'failure';
  }
  return 'success';
}

function diagnosticsSeverity(outcome){
  if(outcome==='failure')return 'error';
  if(outcome==='partial'||outcome==='offline')return 'warn';
  return 'info';
}

function safeProperties(name,input={}){
  const common={
    ...(input.sessionId?{session_ref:liveSessionRef(input.sessionId)}:{})
  };
  switch(name){
    case 'live_action_result':{
      const action=String(input.action||'').trim().toLowerCase();
      if(!ACTIONS.has(action))throw new TypeError('Unsupported Live observability action');
      const outcome=String(input.outcome||'').trim().toLowerCase()==='failure'?'failure':'success';
      return Object.freeze({
        ...common,
        action,
        outcome,
        ...(outcome==='failure'&&errorCode(input.errorCode)?{error_code:errorCode(input.errorCode)}:{})
      });
    }
    case 'live_sync_result':
      return Object.freeze({
        status:['success','partial','failure','offline','unavailable'].includes(String(input.status||'').toLowerCase())
          ?String(input.status).toLowerCase()
          :'failure',
        queue_before:bounded(input.queueBefore),
        queue_after:bounded(input.queueAfter),
        processed:bounded(input.processed),
        retries:bounded(input.retries),
        conflicts:bounded(input.conflicts),
        ...(errorCode(input.errorCode)?{error_code:errorCode(input.errorCode)}:{})
      });
    case 'live_sync_recovery':
      return Object.freeze({
        queue_before:bounded(input.queueBefore),
        queue_after:bounded(input.queueAfter),
        recovered_count:bounded(input.recoveredCount)
      });
    case 'live_notification_interaction':{
      const kind=String(input.kind||'').trim().toLowerCase();
      return Object.freeze({
        notification_kind:NOTIFICATION_KINDS.has(kind)?kind:'unknown',
        action:String(input.action||'').trim().toLowerCase()==='tap'?'tap':'other'
      });
    }
    default:
      throw new TypeError('Unsupported Live operational event');
  }
}

export function createLiveObservability({
  appVersion='unknown',
  platform='web',
  environment='dev',
  diagnostics=null,
  sink=()=>{},
  now=()=>new Date().toISOString(),
  maxEvents=240
}={}){
  const limit=Math.max(20,Math.min(2000,Number(maxEvents)||240));
  const records=[];

  function record(name,input={}){
    if(!ALLOWED.has(name))throw new TypeError('Unsupported Live operational event');
    const properties=safeProperties(name,input);
    const event=Object.freeze({
      name,
      at:String(now()),
      product:'dymistrz',
      app_version:contextValue(appVersion),
      platform:contextValue(platform),
      environment:contextValue(environment),
      properties
    });
    records.push(event);
    if(records.length>limit)records.splice(0,records.length-limit);

    const outcome=diagnosticsOutcome(name,properties);
    try{
      diagnostics?.record?.({
        severity:diagnosticsSeverity(outcome),
        module:'live',
        component:'observability',
        eventCode:name,
        outcome,
        correlationId:properties.session_ref||null,
        flowId:input.flowId||null,
        networkState:properties.status==='offline'?'offline':undefined,
        syncState:name==='live_sync_result'?properties.status:(name==='live_sync_recovery'?'success':undefined),
        meta:properties
      });
    }catch(error){
      try{diagnostics?.capture?.('live.observability.diagnostics',error,{flowId:input.flowId||null});}catch{}
    }

    try{sink?.(event);}catch(error){
      try{diagnostics?.capture?.('live.observability.sink',error,{flowId:input.flowId||null});}catch{}
    }
    return event;
  }

  function recordAction(input={}){return record('live_action_result',input);}
  function recordSync(input={}){return record('live_sync_result',input);}
  function recordSyncRecovery(input={}){return record('live_sync_recovery',input);}
  function recordNotificationInteraction(input={}){return record('live_notification_interaction',input);}

  function health({minimumAttempts=4,failureRateThreshold=0.25}={}){
    const minimum=Math.max(1,Math.floor(Number(minimumAttempts)||4));
    const rawThreshold=Number(failureRateThreshold);
    const threshold=Math.max(0,Math.min(1,Number.isFinite(rawThreshold)?rawThreshold:0.25));
    const sessions=new Map();

    for(const event of records){
      if(event.name!=='live_action_result')continue;
      const ref=event.properties.session_ref;
      if(!ref)continue;
      const current=sessions.get(ref)||{session_ref:ref,attempts:0,failures:0};
      current.attempts+=1;
      if(event.properties.outcome==='failure')current.failures+=1;
      sessions.set(ref,current);
    }

    const rows=[...sessions.values()].map(row=>{
      const failureRate=row.attempts?row.failures/row.attempts:0;
      return Object.freeze({
        ...row,
        failure_rate:failureRate,
        abnormal:row.attempts>=minimum&&failureRate>=threshold
      });
    }).sort((a,b)=>b.failure_rate-a.failure_rate||b.attempts-a.attempts||a.session_ref.localeCompare(b.session_ref));

    const sync=records.filter(event=>event.name==='live_sync_result');
    const syncFailures=sync.filter(event=>['failure','partial'].includes(event.properties.status)).length;
    return Object.freeze({
      generated_at:String(now()),
      sessions:Object.freeze(rows),
      abnormal_sessions:Object.freeze(rows.filter(row=>row.abnormal)),
      sync:Object.freeze({
        attempts:sync.length,
        failures:syncFailures,
        failure_rate:sync.length?syncFailures/sync.length:0,
        recoveries:records.filter(event=>event.name==='live_sync_recovery').length
      })
    });
  }

  return Object.freeze({
    record,
    recordAction,
    recordSync,
    recordSyncRecovery,
    recordNotificationInteraction,
    health,
    events:()=>Object.freeze([...records]),
    clear:()=>records.splice(0,records.length)
  });
}
